/**
 * ============================================================================
 * ADMIN DISPUTE MANAGEMENT PAGE
 * ============================================================================
 * Same structure and UX as Admin Transactions: hero, stats bento, filters,
 * dispute list, pagination.
 */

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '../../hooks/useAdmin';
import { useAdminStore } from '../../store';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import AdminLayout from '../../components/admin/AdminLayout';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import Spinner from '../../components/common/Spinner';
import StatusBadge from '../../components/common/StatusBadge';
import '../../styles/landing.css';
import '../../styles/animations.css';
import '../../styles/global.css';

/** Normalize backend dispute (snake_case + nested) for display */
function normalizeDispute(d) {
  if (!d) return null;
  const tx = d.transaction || {};
  const raised = d.raised_by_user || d.raised_by || {};
  const against = d.against_user_user || d.against_user || {};
  return {
    ...d,
    disputeId: d.dispute_id ?? d.disputeId,
    createdAt: d.opened_at ?? d.created_at ?? d.createdAt,
    amount: tx.amount ?? d.amount,
    buyerName: raised.full_name ?? raised.fullName ?? d.buyerName ?? 'N/A',
    sellerName: against.full_name ?? against.fullName ?? d.sellerName ?? 'N/A',
    title: d.title ?? tx.title ?? '',
  };
}

const AdminDisputes = () => {
  const navigate = useNavigate();
  const { listDisputes, disputes, loading } = useAdmin();
  const { filters, setFilters, pagination, setPagination } = useAdminStore();
  const { showError } = useToast();
  const { formatCurrency, formatDate, language } = useI18n();

  const [searchQuery, setSearchQuery] = useState(filters?.search || '');
  const [isLoading, setIsLoading] = useState(true);
  const [statsInView, setStatsInView] = useState(false);
  const statsRef = useRef(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const result = await listDisputes({
          ...filters,
          search: searchQuery || undefined,
          status: filters?.status || undefined,
          page: pagination?.page || 1,
          limit: pagination?.pageSize || 20,
        });
        if (result?.pagination) {
          setPagination({ ...pagination, ...result.pagination });
        }
      } catch (error) {
        showError('Failed to load disputes');
        console.error('Load disputes error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [
    filters,
    searchQuery,
    listDisputes,
    showError,
    pagination?.page,
    pagination?.pageSize,
  ]);

  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => e.isIntersecting && setStatsInView(true));
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setFilters({ ...filters, search: searchQuery });
    setPagination({ ...pagination, page: 1 });
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
    setPagination({ ...pagination, page: 1 });
  };

  const clearFilters = () => {
    setFilters({ search: null, status: null });
    setSearchQuery('');
    setPagination({ ...pagination, page: 1 });
  };

  const getPriorityVariant = (priority) => {
    const variants = { urgent: 'error', high: 'warning', medium: 'info', normal: 'default' };
    return variants[priority] || 'default';
  };

  const statusOptions = [
    { value: '', label: language === 'fr' ? 'Tous les statuts' : 'All Statuses' },
    { value: 'open', label: language === 'fr' ? 'Ouvert' : 'Open' },
    { value: 'negotiating', label: language === 'fr' ? 'Négociation' : 'Negotiating' },
    { value: 'mediation_requested', label: language === 'fr' ? 'Médiation demandée' : 'Mediation Requested' },
    { value: 'under_mediation', label: language === 'fr' ? 'Sous médiation' : 'Under Mediation' },
    { value: 'resolved', label: language === 'fr' ? 'Résolu' : 'Resolved' },
    { value: 'closed', label: language === 'fr' ? 'Fermé' : 'Closed' },
  ];

  const list = Array.isArray(disputes) ? disputes : [];
  const stats = {
    total: pagination?.total ?? list.length,
    active: list.filter((d) => ['open', 'negotiating'].includes(d?.status)).length,
    inMediation: list.filter((d) => ['mediation_requested', 'under_mediation'].includes(d?.status)).length,
    resolved: list.filter((d) => d?.status === 'resolved').length,
  };

  const totalFromPagination = pagination?.total ?? list.length;
  const showPagination = totalFromPagination > (pagination?.pageSize ?? 20);

  if (isLoading || loading) {
    return (
      <AdminLayout hideFooter>
        <div className="transactions-wt transactions-wt-loading">
          <div className="dashboard-wt-hero-inner">
            <Spinner size="lg" />
            <p className="mt-4 text-[#a3a3a3]">
              {language === 'fr' ? 'Chargement des litiges...' : 'Loading disputes...'}
            </p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="transactions-wt dashboard-wt">
        {/* Hero */}
        <section className="dashboard-wt-hero">
          <div className="dashboard-wt-hero-bg" aria-hidden="true" />
          <div className="dashboard-wt-hero-grid" aria-hidden="true" />
          <div className="dashboard-wt-hero-noise" aria-hidden="true" />
          <div className="dashboard-wt-hero-orb dashboard-wt-hero-orb-1" aria-hidden="true" />
          <div className="dashboard-wt-hero-orb dashboard-wt-hero-orb-2" aria-hidden="true" />

          <div className="dashboard-wt-hero-inner">
            <h1 className="dashboard-wt-hero-title">
              ⚠️ {language === 'fr' ? 'Gestion des litiges' : 'Dispute Management'}
            </h1>
            <p className="dashboard-wt-hero-sub">
              {language === 'fr'
                ? 'Résolvez les litiges et médiations'
                : 'Resolve disputes and mediations'}
            </p>
            <button
              type="button"
              className="dashboard-wt-btn-primary dashboard-wt-btn-large mt-6"
              onClick={() => navigate('/admin/dashboard')}
            >
              <span className="dashboard-wt-btn-icon">←</span>
              {language === 'fr' ? 'Retour au tableau de bord' : 'Back to Dashboard'}
            </button>
          </div>
        </section>

        {/* Stats - Bento */}
        <section className="dashboard-wt-section">
          <div className="dashboard-wt-section-inner">
            <div className={`dashboard-wt-stats ${statsInView ? 'is-inview' : ''}`} ref={statsRef}>
              {[
                { icon: '📋', label: language === 'fr' ? 'Total' : 'Total', value: stats.total },
                { icon: '⚠️', label: language === 'fr' ? 'Actifs' : 'Active', value: stats.active },
                { icon: '🔔', label: language === 'fr' ? 'Médiation' : 'In Mediation', value: stats.inMediation },
                { icon: '✅', label: language === 'fr' ? 'Résolus' : 'Resolved', value: stats.resolved },
              ].map((stat, i) => (
                <div
                  key={i}
                  className="dashboard-wt-stat-card"
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  <div className="dashboard-wt-stat-icon">
                    <span>{stat.icon}</span>
                  </div>
                  <div className="dashboard-wt-stat-content">
                    <div className="dashboard-wt-stat-value">{stat.value}</div>
                    <div className="dashboard-wt-stat-label">{stat.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Filters */}
        <section className="dashboard-wt-section dashboard-wt-section-alt">
          <div className="dashboard-wt-section-inner">
            <div className="transactions-wt-filters dashboard-wt-card">
              <div className="dashboard-wt-card-header">
                <h2 className="dashboard-wt-card-title">
                  {language === 'fr' ? 'Filtres' : 'Filters'}
                </h2>
              </div>
              <div className="dashboard-wt-card-content">
                <form onSubmit={handleSearch} className="transactions-wt-form">
                  <div className="transactions-wt-form-row">
                    <div className="transactions-wt-form-search">
                      <Input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={
                          language === 'fr' ? 'ID litige, titre...' : 'Dispute ID, title...'
                        }
                        className="transactions-wt-input"
                      />
                    </div>
                    <div className="transactions-wt-form-select">
                      <Select
                        value={filters?.status || ''}
                        onChange={(e) =>
                          handleFilterChange('status', e.target.value || null)
                        }
                        options={statusOptions}
                      />
                    </div>
                    <button type="submit" className="dashboard-wt-btn-primary">
                      {language === 'fr' ? 'Rechercher' : 'Search'}
                    </button>
                    {(filters?.search || filters?.status) && (
                      <button
                        type="button"
                        className="dashboard-wt-btn-secondary"
                        onClick={clearFilters}
                      >
                        {language === 'fr' ? 'Effacer' : 'Clear'}
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </div>

            {/* List or empty */}
            {list.length === 0 ? (
              <div className="dashboard-wt-card dashboard-wt-empty">
                <div className="dashboard-wt-empty-icon">⚠️</div>
                <h3 className="dashboard-wt-empty-title">
                  {language === 'fr' ? 'Aucun litige' : 'No disputes found'}
                </h3>
                <p className="dashboard-wt-empty-desc">
                  {language === 'fr'
                    ? 'Aucun litige ne correspond à vos filtres'
                    : 'No disputes match your filters'}
                </p>
                <button
                  type="button"
                  className="dashboard-wt-btn-secondary dashboard-wt-btn-large"
                  onClick={clearFilters}
                >
                  {language === 'fr' ? 'Effacer les filtres' : 'Clear Filters'}
                </button>
              </div>
            ) : (
              <>
                <div className="dashboard-wt-transactions transactions-wt-list">
                  {list.map((dispute, index) => {
                    const d = normalizeDispute(dispute);
                    return (
                      <div
                        key={dispute.id}
                        className="dashboard-wt-transaction-wrapper dashboard-wt-card"
                        style={{ animationDelay: `${index * 0.05}s` }}
                      >
                        <div className="dashboard-wt-card-content">
                          <div className="flex flex-wrap items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500/80 to-orange-600/80 flex items-center justify-center text-xl shadow-lg flex-shrink-0">
                              ⚠️
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2 mb-1">
                                <h3 className="font-bold text-[#e5e5e5]">
                                  {d.disputeId ?? dispute.id}
                                </h3>
                                <StatusBadge
                                  variant={getPriorityVariant(d.priority)}
                                  size="sm"
                                >
                                  {(d.priority || 'normal').toUpperCase()}
                                </StatusBadge>
                                <StatusBadge
                                  variant={
                                    d.status === 'resolved'
                                      ? 'success'
                                      : d.status === 'open' || d.status === 'negotiating'
                                      ? 'warning'
                                      : 'info'
                                  }
                                  size="sm"
                                >
                                  {(d.status || '').toUpperCase()}
                                </StatusBadge>
                              </div>
                              <p className="text-sm text-[#a3a3a3] mb-1">
                                {d.buyerName} vs {d.sellerName}
                                {d.title ? ` · ${d.title}` : ''}
                              </p>
                              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#737373]">
                                <span>
                                  {language === 'fr' ? 'Montant' : 'Amount'}:{' '}
                                  {formatCurrency(d.amount ?? 0)}
                                </span>
                                <span>·</span>
                                <span>
                                  {d.createdAt && formatDate(d.createdAt)}
                                </span>
                              </div>
                            </div>
                            <button
                              type="button"
                              className="dashboard-wt-btn-primary flex-shrink-0"
                              onClick={() => navigate(`/admin/disputes/${dispute.id}`)}
                            >
                              {language === 'fr' ? 'Examiner' : 'Review'} →
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {showPagination && (
                  <div className="transactions-wt-pagination dashboard-wt-card">
                    <div className="transactions-wt-pagination-inner">
                      <span className="transactions-wt-pagination-text">
                        {language === 'fr' ? 'Affichage' : 'Showing'}{' '}
                        {(pagination.page - 1) * (pagination.pageSize || 20) + 1}{' '}
                        {language === 'fr' ? 'à' : 'to'}{' '}
                        {Math.min(
                          pagination.page * (pagination.pageSize || 20),
                          totalFromPagination
                        )}{' '}
                        {language === 'fr' ? 'sur' : 'of'}{' '}
                        {totalFromPagination}
                      </span>
                      <div className="transactions-wt-pagination-btns">
                        <button
                          type="button"
                          className="dashboard-wt-btn-secondary"
                          onClick={() =>
                            setPagination({ ...pagination, page: pagination.page - 1 })
                          }
                          disabled={pagination.page <= 1}
                        >
                          {language === 'fr' ? 'Précédent' : 'Previous'}
                        </button>
                        <button
                          type="button"
                          className="dashboard-wt-btn-secondary"
                          onClick={() =>
                            setPagination({ ...pagination, page: pagination.page + 1 })
                          }
                          disabled={
                            pagination.page * (pagination.pageSize || 20) >=
                            totalFromPagination
                          }
                        >
                          {language === 'fr' ? 'Suivant' : 'Next'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </div>
    </AdminLayout>
  );
};

export default AdminDisputes;
